import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import * as AuthActions from './store/auth.action';
import { AuthState, userMenuAccess } from './store/auth.model';
import { selectToken } from './store/auth.selector';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ToasterService } from '../providers/toaster/toaster.service';
import { HelperServiceService } from '../utils/helper-service.service';

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private readonly AUTH_KEY = 'auth_data';
  private readonly MASTER_USER_ID_KEY = 'master_user_id';
  private apiUrl = environment.apiUrl;
  private baseUrl = this.apiUrl.replace(/\/client\/$/,"");
  private token: string | null = null;
  constructor(private store: Store<AuthState>, private http: HttpClient, private router: Router, private toasterService: ToasterService,private helperService: HelperServiceService) {
    this.store.select(selectToken).subscribe((tk) => {
      this.token = tk;
    });
  }

  login(email: string, password: string): Observable<any> {
    this.store.dispatch(AuthActions.login({ email, password }));

    return this.http.post(`${this.baseUrl}/login`, { email, password, action_for: 'admin' }).pipe(
      tap({
        next: (response: any) => {
          if (response.status === 'success') {

            const { token, user } = response.data;
            this.store.dispatch(AuthActions.loginSuccess({ user, token }));
            let userMenu!: userMenuAccess
            userMenu = this.helperService.buildMenuTree(response.data.menus);
            this.store.dispatch(AuthActions.setPermissionsRole({ permissions: userMenu }));

            this.saveAuthDataToLocalStorage(user, token,userMenu);
 this.router.navigate(['/dashboard']);
            // Fetch and persist master user id after login
            const loggedInUserId = user?.id;
            if (loggedInUserId != null) {
              this.fetchAndSaveMasterUserId(Number(loggedInUserId), token);
            }

            // Navigation and toaster moved to login component
          } else {
            this.store.dispatch(AuthActions.loginFailure({ error: response.message }));
            // Toaster moved to login component
          }
        },
        error: (error) => {
          // Toaster moved to login component
          this.store.dispatch(AuthActions.loginFailure({ error: error.message }));
        }
      })
    );
  }

  logout(): void {
    this.store.dispatch(AuthActions.logout());
    this.clearAuthDataFromLocalStorage();
    this.toasterService.setToaster('success', 'Logged out successfully', 'You have been logged out successfully.');
    this.router.navigate(['/login']);
  }


  loadAuthDataFromLocalStorage(): void {
    const authData = localStorage.getItem(this.AUTH_KEY);
    if (authData) {
      const { user, token,permissions } = JSON.parse(authData);
      this.store.dispatch(AuthActions.loadAuthDataFromLocalStorage({ user, token,permissions }));

    } else {
      this.store.dispatch(AuthActions.logout()); // or a specific reset action
    }
  }



  isUserAuthenticated(): Observable<boolean> {
    return this.store.select(selectToken).pipe(map(token => !!token));
  }


  private saveAuthDataToLocalStorage(user: any, token: string, permissions: userMenuAccess): void {
    const authData = { user, token,permissions };
    localStorage.setItem(this.AUTH_KEY, JSON.stringify(authData));
  }

  private clearAuthDataFromLocalStorage(): void {
    localStorage.removeItem(this.AUTH_KEY);
    localStorage.removeItem(this.MASTER_USER_ID_KEY);
  }

  private saveMasterUserIdToLocalStorage(masterUserId: number): void {
    localStorage.setItem(this.MASTER_USER_ID_KEY, String(masterUserId));
  }

  private fetchAndSaveMasterUserId(masterUserId: number, token: string): void {
    const url = `${this.baseUrl}/client/user/get-by-master-user-id/${masterUserId}`;

    this.http
      .get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .subscribe({
        next: (response: any) => {
          if (response?.success && response?.data?.id != null) {
            this.saveMasterUserIdToLocalStorage(response.data.id);
          }
        },
        error: () => {
          // Intentionally ignore to not block login flow
        },
      });
  }

  getToken(): string | null {
    return this.token;
  }

  getHeader(): Observable<HttpHeaders> {
    return this.store.select(selectToken).pipe(
      map((token) => {
        if (!token) {
          const authData = localStorage.getItem(this.AUTH_KEY);
          if (authData) {
            const { token } = JSON.parse(authData);
            if (token) {
              return new HttpHeaders({
                Authorization: `Bearer ${token}`,
              });
            }
          }
          this.logout();
        }
        return new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });
      })
    );
  }

  tokenExpire() {
    this.logout();
    this.toasterService.setToaster('error', 'Session Expired', 'The session has expired. Please log in again.');
  }

}
